import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, FileText, Search, Trash2, Loader2,
  Lightbulb, BookOpen, Heart, Calendar, Pen
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";
// NOTES-V2 — novos componentes/utils. Tudo atrás de feature flags.
import HeroNoteCard from "@/components/notes/HeroNoteCard";
import MobileCollapse from "@/components/shared/MobileCollapse";  // FaseB B2
import NotificationBell from "@/components/shared/NotificationBell";  // FaseC C1
import NoteGroup from "@/components/notes/NoteGroup";
import NoteCardDifferentiated from "@/components/notes/NoteCardDifferentiated";
// PRODUTO-V1 N7 — editor full modal.
import NoteEditorModal from "@/components/notes/NoteEditorModal";
import {
  NOTE_TYPES,
  groupNotes,
  DATE_GROUP_ORDER,
  DATE_GROUP_LABELS,
  DATE_GROUP_COLORS,
  TYPE_GROUP_ORDER,
  TYPE_GROUP_LABELS,
} from "@/utils/noteGrouping";
import { dedupNotes } from "@/utils/noteHash";
import trace from "@/services/executionTrace";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NOTES-V2 — feature flags. Default true; setar "false" no .env do
// frontend pra rollback granular sem deploy.
const FLAG_HERO = (process.env.REACT_APP_NOTES_HERO ?? "true") !== "false";
const FLAG_GROUPING = (process.env.REACT_APP_NOTES_GROUPING ?? "true") !== "false";
const FLAG_DIFF_CARDS = (process.env.REACT_APP_NOTES_DIFFERENTIATED_CARDS ?? "true") !== "false";
const FLAG_MARKDOWN = (process.env.REACT_APP_NOTES_MARKDOWN ?? "true") !== "false";
const FLAG_DEDUP = (process.env.REACT_APP_NOTES_DEDUP ?? "true") !== "false";
// PRODUTO-V1 — N6/N7 toggles. Default true; setar "false" pra rollback.
const FLAG_NOTES_PIN = (process.env.REACT_APP_NOTES_PIN ?? "true") !== "false";
const FLAG_NOTES_EDITOR = (process.env.REACT_APP_NOTES_EDITOR ?? "true") !== "false";

const GROUPING_MODE_KEY = "nexo_notes_grouping";

/**
 * Security audit (FIN-2 T1, 2026-04-30) — sections legacy.
 *
 * Backend já blindado pela SEC-1. Sem JWT, fetch retorna 401 e UI quebra.
 *
 * | Endpoint chamado     | authFetch usado? | Notas             |
 * |----------------------|------------------|-------------------|
 * | GET  /api/notes      | ❌ → ✅ (T2)     | listar notas      |
 * | POST /api/notes      | ❌ → ✅ (T2)     | criar nota        |
 * | DEL  /api/notes/:id  | ❌ → ✅ (T2)     | deletar nota      |
 */

// NOTES-V2 N3 — noteTypes expandido com research + daily_review.
// Notas vindas do agent (web_search, briefing diário) deixam de cair
// no fallback "geral" e ganham label + cor próprias.
// Mantém a constante local pra compat com legacy NoteCard/filter UI;
// utils/noteGrouping.js é a fonte canônica pra novos componentes.
const noteTypes = {
  idea: { label: "Ideia", icon: Lightbulb, color: "#f59e0b", bg: "bg-amber-500/10" },
  journal: { label: "Diário", icon: BookOpen, color: "#a855f7", bg: "bg-purple-500/10" },
  vent: { label: "Desabafo", icon: Heart, color: "#ec4899", bg: "bg-pink-500/10" },
  research: { label: "Pesquisa", icon: Search, color: "#3b82f6", bg: "bg-blue-500/10" },
  general: { label: "Geral", icon: FileText, color: "#06b6d4", bg: "bg-cyan-500/10" },
};

function NoteCard({ note, onDelete, onClick }) {
  const type = noteTypes[note.type] || noteTypes.general;
  const Icon = type.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diffD = Math.floor((now - d) / 86400000);
    if (diffD === 0) return "Hoje";
    if (diffD === 1) return "Ontem";
    if (diffD < 7) return `${diffD} dias atrás`;
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      whileHover={{ y: -3 }}
      className="glass-card glow-hover rounded-2xl p-5 cursor-pointer group flex flex-col"
      onClick={() => onClick?.(note)}
      data-testid={`note-card-${note._id || note.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${type.color}10` }}>
          <Icon className="w-4 h-4" style={{ color: type.color }} />
        </div>
        <button onClick={(e) => { e.stopPropagation(); onDelete(note); }}
          className="p-1.5 rounded-lg text-slate-700 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
          data-testid={`delete-note-${note._id || note.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Content */}
      <p className="text-sm text-slate-300 line-clamp-5 flex-1 leading-relaxed">{note.content}</p>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.04]">
        <span className="px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider" style={{ backgroundColor: `${type.color}10`, color: type.color }}>
          {type.label}
        </span>
        <span className="text-[11px] text-slate-600">{formatDate(note.created_at)}</span>
      </div>
    </motion.div>
  );
}

function CreateNoteModal({ isOpen, onClose, onSubmit }) {
  const [content, setContent] = useState("");
  const [type, setType] = useState("general");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) { setContent(""); setType("general"); }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault(); if (!content.trim()) return;
    setIsSubmitting(true);
    await onSubmit({ content, type });
    setIsSubmitting(false); onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()} className="bg-[#0d1220] border border-white/[0.08] rounded-2xl p-6 safe-modal-pb w-full max-w-lg">
            <h3 className="font-display text-xl font-bold text-white mb-5">Nova Nota</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-slate-500 mb-2 uppercase tracking-wider">Tipo</label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(noteTypes).map(([key, value]) => {
                    const TypeIcon = value.icon;
                    return (
                      <button key={key} type="button" onClick={() => setType(key)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-all
                          ${type === key ? "border-white/[0.15] bg-white/[0.05]" : "border-white/[0.04] text-slate-500 hover:text-white hover:bg-white/[0.02]"}`}
                        style={type === key ? { color: value.color } : {}}>
                        <TypeIcon className="w-4 h-4" /> {value.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Conteúdo</label>
                <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="O que está na sua mente?"
                  rows={6} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors resize-none" autoFocus />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 px-4 py-3 rounded-xl border border-white/[0.06] text-slate-400 hover:text-white hover:bg-white/5 transition-colors">Cancelar</button>
                <button type="submit" disabled={!content.trim() || isSubmitting}
                  className="flex-1 px-4 py-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/15 disabled:opacity-40 transition-all flex items-center justify-center gap-2 font-medium">
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function NotesSection() {
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  // NOTES-V2 N4 — modo de agrupamento (date|type) persistido.
  const [groupingMode, setGroupingMode] = useState(() => {
    try {
      const raw = localStorage.getItem(GROUPING_MODE_KEY);
      return raw === "type" ? "type" : "date";
    } catch { return "date"; }
  });

  const fetchNotes = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/notes`);
      if (res.ok) { const data = await res.json(); setNotes(data.notes || []); }
    } catch (err) { console.error("Error fetching notes:", err); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleCreate = async (data) => {
    try {
      const res = await authFetch(`${API_URL}/api/notes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
      if (res.ok) { toast.success("Nota salva!"); fetchNotes(); }
    } catch (err) { toast.error("Erro ao salvar"); }
  };

  const handleDelete = async (note) => {
    if (!window.confirm("Excluir esta nota?")) return;
    try {
      const res = await authFetch(`${API_URL}/api/notes/${note._id || note.id}`, { method: "DELETE" });
      if (res.ok) { setNotes(prev => prev.filter(n => (n._id || n.id) !== (note._id || note.id))); toast.success("Excluída"); }
    } catch (err) { console.error("Error deleting:", err); }
  };

  // PRODUTO-V1 N6 — pin/desafixar otimista. Atualiza state local antes
  // do PUT, reverte se backend falhar. Bem mais responsivo que esperar.
  const handlePin = async (note, newPinned) => {
    const noteId = note._id || note.id;
    setNotes(prev => prev.map(n =>
      (n._id || n.id) === noteId ? { ...n, pinned: !!newPinned } : n,
    ));
    try {
      const res = await authFetch(`${API_URL}/api/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pinned: !!newPinned }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(newPinned ? "Fixada" : "Desafixada");
    } catch (err) {
      // Revert
      setNotes(prev => prev.map(n =>
        (n._id || n.id) === noteId ? { ...n, pinned: !newPinned } : n,
      ));
      toast.error("Não consegui atualizar — tenta de novo");
    }
  };

  // PRODUTO-V1 N7 — editor modal state.
  const [editingNote, setEditingNote] = useState(null);
  const handleOpenEditor = (note) => {
    setEditingNote(note);
  };
  const handleEditorClose = () => setEditingNote(null);
  const handleEditorSave = async (changes) => {
    if (!editingNote) return false;
    const noteId = editingNote._id || editingNote.id;
    try {
      const res = await authFetch(`${API_URL}/api/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(changes),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setNotes(prev => prev.map(n =>
        (n._id || n.id) === noteId ? { ...n, ...updated } : n,
      ));
      toast.success("Salvo");
      return true;
    } catch (err) {
      toast.error("Erro ao salvar — tenta de novo");
      return false;
    }
  };

  // NOTES-V2 N5 — dedup automático ANTES de qualquer filter/search.
  // Notas com mesmo content+type criadas em janela de 30s viram 1
  // visível + counter "+N duplicatas escondidas". Bug do agent
  // chamando notes_create 2x fica mascarado até fix backend.
  const dedupResult = useMemo(() => {
    if (!FLAG_DEDUP) return { deduped: notes, hiddenCount: 0 };
    return dedupNotes(notes);
  }, [notes]);

  // Trace 1× por fetch quando dedup detectou duplicatas.
  useEffect(() => {
    if (isLoading) return;
    if (dedupResult.hiddenCount > 0) {
      try {
        trace.record("notes.dedup.hidden_count", {
          hidden: dedupResult.hiddenCount,
          total: notes.length,
        });
      } catch { /* */ }
    }
  }, [dedupResult.hiddenCount, isLoading, notes.length]);

  const filteredNotes = dedupResult.deduped.filter(n => {
    const matchFilter = filter === "all" || n.type === filter;
    const matchSearch = !searchQuery || n.content?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchFilter && matchSearch;
  });

  // NOTES-V2 — trace de render (1× por mudança de filter/total/mode).
  useEffect(() => {
    if (isLoading) return;
    try {
      trace.record("notes.list.rendered", {
        total_count: filteredNotes.length,
        filter,
        mode: FLAG_GROUPING ? groupingMode : "flat",
      });
    } catch { /* */ }
  }, [filter, groupingMode, isLoading, notes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGroupingModeChange = (mode) => {
    setGroupingMode(mode);
    try { localStorage.setItem(GROUPING_MODE_KEY, mode); } catch {/* */}
    try { trace.record("notes.group.mode_changed", { mode }); } catch {/* */}
  };

  return (
    <div className="p-6 md:p-8 pb-32 max-w-6xl mx-auto" data-testid="notes-section">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-wide">Notas</h1>
          <p className="text-xs text-slate-500 mt-1">{notes.length} nota{notes.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <NotificationBell />
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/15 transition-all text-sm font-medium" data-testid="create-note-btn">
            <Plus className="w-4 h-4" /> Nova Nota
          </motion.button>
        </div>
      </div>

      {/* NOTES-V2 N1 — Hero "Última Captura". FaseB B2: colapsado em mobile. */}
      {FLAG_HERO && !isLoading && (
        <MobileCollapse title="ÚLTIMA CAPTURA" defaultOpen={false} desktopTitle={false} className="mb-6">
          <HeroNoteCard
            notes={dedupResult.deduped}
            onDelete={handleDelete}
            onAskNexo={() => {
              toast.info("Pergunte ao Nexo no chat: 'o que devo escrever hoje?'");
            }}
            // PRODUTO-V1 N6/N7 — handlers reais.
            onPin={FLAG_NOTES_PIN ? handlePin : undefined}
            onOpenEditor={FLAG_NOTES_EDITOR ? handleOpenEditor : undefined}
          />
        </MobileCollapse>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
          <input type="text" placeholder="Buscar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 text-sm transition-colors" data-testid="notes-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === "all" ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}>Todas</button>
          {Object.entries(noteTypes).map(([key, value]) => (
            <button key={key} onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === key ? "border" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
              style={filter === key ? { backgroundColor: `${value.color}10`, color: value.color, borderColor: `${value.color}30` } : {}}>
              {value.label}
            </button>
          ))}
        </div>
        {/* NOTES-V2 N4 — pill switcher Data/Tipo */}
        {FLAG_GROUPING && (
          <div className="ml-auto flex items-center gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]" data-testid="notes-grouping-toggle">
            <button
              onClick={() => handleGroupingModeChange("date")}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${groupingMode === "date" ? "bg-cyan-500/15 text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}
              data-testid="notes-grouping-date"
            >
              Por Data
            </button>
            <button
              onClick={() => handleGroupingModeChange("type")}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${groupingMode === "type" ? "bg-cyan-500/15 text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}
              data-testid="notes-grouping-type"
            >
              Por Tipo
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
      ) : filteredNotes.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
            <Pen className="w-7 h-7 text-cyan-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-1.5">{searchQuery ? "Nenhuma nota encontrada" : "Nenhuma nota"}</h3>
          <p className="text-slate-600 text-sm mb-5">{searchQuery ? "Tente outro termo" : "Comece a escrever"}</p>
          {!searchQuery && (
            <button onClick={() => setIsModalOpen(true)} className="px-5 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm hover:bg-cyan-500/15 transition-all">Criar Nota</button>
          )}
        </motion.div>
      ) : FLAG_GROUPING ? (
        <div data-testid="notes-list-grouped">
          {(() => {
            // NOTES-V2 N4 — render por grupos colapsáveis (mode = date|type).
            const grouped = groupNotes(filteredNotes, groupingMode);
            const order = groupingMode === "type" ? TYPE_GROUP_ORDER : DATE_GROUP_ORDER;
            const labels = groupingMode === "type" ? TYPE_GROUP_LABELS : DATE_GROUP_LABELS;
            return order.map((gk) => {
              const items = grouped[gk] || [];
              if (items.length === 0) return null;
              // Defaults expanded:
              //   date mode → today + this_week sempre expanded
              //   type mode → todos não-vazios expanded (default false)
              const defaultCollapsed =
                groupingMode === "date"
                  ? !(gk === "today" || gk === "this_week")
                  : false;
              const colors = groupingMode === "type"
                ? null
                : (DATE_GROUP_COLORS[gk] || DATE_GROUP_COLORS.older);
              const colorHex = groupingMode === "type"
                ? (NOTE_TYPES[gk]?.color || "#64748b")
                : null;
              return (
                <NoteGroup
                  key={gk}
                  mode={groupingMode}
                  groupKey={gk}
                  label={labels[gk]}
                  count={items.length}
                  defaultCollapsed={defaultCollapsed}
                  dotCls={colors?.dot}
                  textCls={colors?.text}
                  countCls={colors?.count}
                  colorHex={colorHex}
                >
                  <AnimatePresence mode="popLayout">
                    {items.map((note) =>
                      FLAG_DIFF_CARDS ? (
                        <NoteCardDifferentiated
                          key={note._id || note.id}
                          note={note}
                          onDelete={handleDelete}
                          onClick={FLAG_NOTES_EDITOR ? handleOpenEditor : undefined}
                        />
                      ) : (
                        <NoteCard
                          key={note._id || note.id}
                          note={note}
                          onDelete={handleDelete}
                        />
                      ),
                    )}
                  </AnimatePresence>
                </NoteGroup>
              );
            });
          })()}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="notes-list-flat">
          <AnimatePresence mode="popLayout">
            {filteredNotes.map(note =>
              FLAG_DIFF_CARDS ? (
                <NoteCardDifferentiated
                  key={note._id || note.id}
                  note={note}
                  onDelete={handleDelete}
                />
              ) : (
                <NoteCard
                  key={note._id || note.id}
                  note={note}
                  onDelete={handleDelete}
                  onClick={FLAG_NOTES_EDITOR ? handleOpenEditor : undefined}
                />
              ),
            )}
          </AnimatePresence>
        </div>
      )}

      <CreateNoteModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleCreate} />

      {/* PRODUTO-V1 N7 — editor modal full. Só monta com flag ON e nota
          selecionada (HeroNoteCard "Abrir em foco" ou click no card). */}
      {FLAG_NOTES_EDITOR && editingNote && (
        <NoteEditorModal
          note={editingNote}
          isOpen={!!editingNote}
          onClose={handleEditorClose}
          onSave={handleEditorSave}
        />
      )}
    </div>
  );
}
